/**
 * Language Switcher Component
 * Allows users to switch between supported languages
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { getLocaleFromPathname } from '@/lib/i18n';
import { getLocaleOptions } from '@/lib/i18n/locale-options';
import { useLocale } from 'next-intl';

const languageSwitcherButtonVariants = cva(
  'flex items-center gap-2 touch-manipulation transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/35 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'px-3 py-2 text-seasalt hover:text-orange-peel focus-visible:ring-orange-peel focus-visible:ring-2 focus-visible:ring-offset-background',
        ghost:
          'rounded-md px-2 py-1 text-muted-foreground hover:bg-muted/50 hover:text-foreground focus-visible:ring-orange-peel focus-visible:ring-2 focus-visible:ring-offset-background',
        outline:
          'rounded-md border border-input px-3 py-2 hover:bg-accent focus-visible:ring-orange-peel focus-visible:ring-2 focus-visible:ring-offset-background',
        /** Header toolbar — borderless; focus uses navy ring (not orange) to match nav chrome */
        header:
          'h-9 min-h-0 shrink-0 gap-1.5 rounded-lg border-0 bg-transparent px-3 text-sm font-medium leading-none tracking-[0.02em] text-oxford-blue/90 shadow-none hover:bg-oxford-blue/[0.04] hover:text-oxford-blue focus-visible:ring-2 focus-visible:ring-oxford-blue/35 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        /** Mobile drawer — light surface, matches header chrome */
        drawerBottom:
          'h-9 min-h-0 shrink-0 gap-1.5 rounded-lg border-0 bg-transparent px-3 text-sm font-medium leading-none tracking-[0.02em] text-oxford-blue/90 shadow-none hover:bg-oxford-blue/[0.04] hover:text-oxford-blue focus-visible:ring-2 focus-visible:ring-oxford-blue/35 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        sheet:
          'h-11 w-full min-w-0 justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-left text-sm font-medium text-white/80 hover:bg-white/[0.06] hover:text-white/95 focus-visible:ring-offset-black',
      },
      size: {
        /** Immersive variants (header / drawer / sheet) bake in padding — do not combine with sm/md/lg */
        none: '',
        sm: 'px-2 py-1 text-xs',
        md: 'px-3 py-2',
        lg: 'px-4 py-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

const languageSwitcherMenuVariants = cva('absolute rounded-lg z-[260] overflow-hidden', {
  variants: {
    variant: {
      default:
        'right-0 top-full mt-2 min-w-[11rem] bg-white border border-gray-200/90 shadow-lg shadow-black/5 py-1',
      dark: 'right-0 top-full mt-2 min-w-[11rem] bg-gray-900 border border-gray-700/80 shadow-lg py-1',
      header:
        'right-0 top-full mt-2 min-w-[9rem] border border-oxford-blue/10 bg-white p-1.5 shadow-[0_12px_28px_rgba(0,27,46,0.12)] animate-in fade-in-0 zoom-in-95 duration-150',
      drawerBottom:
        'right-0 bottom-full mb-2 min-w-[9rem] border border-oxford-blue/10 bg-white p-1.5 shadow-[0_12px_28px_rgba(0,27,46,0.12)] animate-in fade-in-0 zoom-in-95 duration-150',
      sheet:
        'left-0 right-0 top-full mt-2 min-w-0 border border-white/[0.12] bg-zinc-950/92 p-2 shadow-[0_12px_28px_rgba(0,0,0,0.28)] backdrop-blur-sm animate-in fade-in-0 zoom-in-95 duration-150',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface LanguageSwitcherProps extends VariantProps<typeof languageSwitcherButtonVariants> {
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
}

export function LanguageSwitcher({
  variant,
  size,
  className,
  open,
  onOpenChange,
  onClose,
}: LanguageSwitcherProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const localeOptions = getLocaleOptions();
  const nextIntlLocale = useLocale();
  // Prefer next-intl's locale state so the button label updates immediately after locale switch.
  // Fallback to pathname parsing for safety if hooks don't sync for some edge case.
  const currentLocale = nextIntlLocale ?? getLocaleFromPathname(pathname);
  const currentLocaleOption =
    localeOptions.find(locale => locale.code === currentLocale) ?? localeOptions[0];

  const formatLocaleCode = (code: string) => (code === 'en' ? 'EN' : code.toUpperCase());
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const isOpen = open ?? internalOpen;

  const setOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      onClose?.();
    }
  };

  const handleLanguageChange = (localeCode: string) => {
    const query =
      typeof window === 'undefined' ? '' : window.location.search.replace(/^\?/, '');
    const href = query ? `${pathname}?${query}` : pathname;
    router.replace(href, { locale: localeCode });
    setOpen(false);
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const ref = containerRef.current;

      if (ref && !ref.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Focus first item when menu opens, restore focus to trigger when it closes
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => firstItemRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    triggerRef.current?.focus();
    return undefined;
  }, [isOpen]);

  const menuVariant =
    variant === 'header'
      ? 'header'
      : variant === 'drawerBottom'
        ? 'drawerBottom'
        : variant === 'sheet'
          ? 'sheet'
          : 'default';
  const isHeaderVariant = variant === 'header';
  const isDrawerBottomVariant = variant === 'drawerBottom';
  const isSheetVariant = variant === 'sheet';
  /** Light chrome shared by header + mobile drawer */
  const isLightChromeVariant = isHeaderVariant || isDrawerBottomVariant;
  const isImmersiveVariant = isLightChromeVariant || isSheetVariant;

  const effectiveSize = isImmersiveVariant ? 'none' : (size ?? 'md');

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setOpen(!isOpen)}
        aria-label='Change language'
        aria-expanded={isOpen}
        aria-haspopup='menu'
        aria-controls='language-switcher-menu'
        className={cn(languageSwitcherButtonVariants({ variant, size: effectiveSize }))}
      >
        {isImmersiveVariant ? (
          <>
            {isSheetVariant && (
              <span
                className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]'
                aria-hidden
              >
                <Globe className='h-4 w-4 text-white/85' aria-hidden />
              </span>
            )}
            {isSheetVariant ? (
              <span className='min-w-0 flex-1 text-left'>
                <span className='block truncate text-[10px] uppercase tracking-[0.02em] leading-none text-white/80'>
                  Locale
                </span>
                <span className='block truncate text-sm font-medium text-white'>
                  {currentLocaleOption.name}
                </span>
              </span>
            ) : (
              <span className='truncate text-left'>{formatLocaleCode(currentLocale)}</span>
            )}
            {isSheetVariant ? (
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-white/80 transition-transform duration-200 ease-out',
                  isOpen && 'rotate-180 text-white/75',
                )}
                aria-hidden
              />
            ) : null}
          </>
        ) : (
          <>
            <Globe className='h-4 w-4 shrink-0 opacity-80' aria-hidden />
            <span className={cn('hidden sm:inline truncate', 'text-sm')}>
              {formatLocaleCode(currentLocale)}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-200 ease-out',
                isOpen && 'rotate-180',
              )}
              aria-hidden
            />
          </>
        )}
      </button>

      {isOpen && (
        <div
          id='language-switcher-menu'
          role='menu'
          aria-label='Select language'
          className={cn(languageSwitcherMenuVariants({ variant: menuVariant }))}
        >
          {localeOptions.map((locale, index) => {
            const isActive = locale.code === currentLocale;
            const label = locale.name;
            return (
              <button
                key={locale.code}
                ref={index === 0 ? firstItemRef : undefined}
                type='button'
                role='menuitemradio'
                aria-checked={isActive}
                onClick={() => handleLanguageChange(locale.code)}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    triggerRef.current?.focus();
                  } else if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLanguageChange(locale.code);
                  }
                }}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between gap-3 rounded-md text-left transition-colors duration-200 ease-out',
                  isLightChromeVariant
                    ? 'mb-0.5 px-3 py-2.5 last:mb-0 text-oxford-blue/80 hover:bg-oxford-blue/[0.04] hover:text-oxford-blue focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-orange-peel/40'
                    : isImmersiveVariant
                      ? 'mb-0.5 px-3 py-2.5 last:mb-0 text-white/82 hover:bg-white/[0.06] hover:text-white/95 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-white/30'
                      : 'mx-0.5 px-4 py-2.5 text-oxford-blue first:rounded-t-[0.65rem] last:rounded-b-[0.65rem] hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
                  isActive &&
                    (isLightChromeVariant
                      ? 'bg-oxford-blue/[0.06] text-oxford-blue'
                      : isImmersiveVariant && 'bg-white/[0.06] text-white/95'),
                )}
                onPointerDown={e => e.stopPropagation()}
              >
                <span
                  className={cn(
                    'flex flex-1 items-center gap-2 text-sm font-medium',
                    !isImmersiveVariant && 'text-oxford-blue',
                  )}
                >
                  <span className='truncate'>{label}</span>
                </span>
                {isActive && (
                  <Check
                    className={cn(
                      'h-4 w-4 shrink-0',
                      isLightChromeVariant ? 'text-orange-peel' : 'text-white/65',
                    )}
                    aria-label='Current language'
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { languageSwitcherButtonVariants, languageSwitcherMenuVariants };
